"use client"

import styled from "@emotion/styled"
import { useInView } from "react-intersection-observer"
import { Container, Desktop, Mobile, NewText, useInViewConfig, useTransitionUpStyles } from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { delayTime, styleObjectToCss } from "ui/transitions/utils"
import { extractImage, extractProperty } from "utils"

const StyledGrid = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 32px;
  padding-bottom: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-bottom: 0px;

    gap: 80px;
  }
`

const StyledPaddingForMobile = styled.div`
  padding: 0 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;
    padding-top: 28px;
  }
`
const StyledAbsoluteImage = styled.div`
  position: relative;
  width: 100%;
  height: 392px;
  ${(props) => props.theme.breakpoints.up("md")} {
    position: absolute;

    width: calc(100vw / 2 - 47.5px);
    left: 0px;
    height: 550px;
  }
`

const StyledRelativeContainer = styled.div`
  position: relative;
  height: 392px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 550px;
  }
`
type Props = { data: any; children: React.ReactNode }

function EnvironmentalHeader({ data, children }: Props) {
  const title = extractProperty(data, "titleH1")
  const innerText = extractProperty(data, "content")
  const iconLetter = extractImage(data, "iconLetter")
  const image = extractImage(data, "image")
  const imageForMobile = extractImage(data, "imageForMobile")

  const { ref, inView } = useInView(useInViewConfig)
  const titleTransitionStyles = useTransitionUpStyles(inView)
  const descriptionTransitionStyles = useTransitionUpStyles(inView, delayTime)

  return (
    <Container withoutPadding containerRef={ref}>
      <StyledGrid>
        <StyledPaddingForMobile>
          {children}
          <ImageWithAlt
            style={{ marginBlockStart: 28, ...titleTransitionStyles }}
            image={iconLetter}
            width={32}
            height={32}
          />
          <NewText
            desktopFontSize={40}
            desktopFontWeight="400"
            desktopLineHeight="110%"
            mobileFontSize={32}
            mobileFontWeight="400"
            mobileLineHeight={"112.5%"}
            component="h1"
            color={"black"}
            cssString={styleObjectToCss(titleTransitionStyles)}
          >
            {title}
          </NewText>
          <NewText
            desktopFontSize={18}
            desktopFontWeight="400"
            desktopLineHeight="25.92px"
            mobileFontSize={18}
            mobileFontWeight="400"
            mobileLineHeight={"25.92px"}
            component="div"
            color={"black"}
            dangerouslySetInnerHTML={{ __html: innerText }}
            style={{ marginBlockStart: 16, padding: 0 }}
            maxWidth={"687px"}
            cssString={styleObjectToCss(descriptionTransitionStyles)}
          />
        </StyledPaddingForMobile>
        <StyledRelativeContainer>
          <StyledAbsoluteImage>
            <Desktop>
              <ImageWithAlt style={{ objectFit: "cover" }} image={image} fill />
            </Desktop>
            <Mobile>
              <ImageWithAlt style={{ objectFit: "cover" }} image={imageForMobile} fill />
            </Mobile>
          </StyledAbsoluteImage>
        </StyledRelativeContainer>
      </StyledGrid>
    </Container>
  )
}

export default EnvironmentalHeader
